import {useContext, useEffect, useState} from 'react';
import {
  Platform,
  PermissionsAndroid,
  Text,
  View,
  StyleSheet,
} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import withIsConnected from '../../hoc/withIsConnected';
import Localized from 'src/constants/AppStrings';
import {IsConnectedProps} from 'src/types/Screen';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import BaseScreen from 'src/components/screens/BaseScreen';
import ScanAndPayLocationsList from 'src/components/elements/home/ScanAndPayLocationsList';
import {CampusLocationType} from 'src/types/CampusLocationTypes';
import LoadingScreen from 'src/components/screens/LoadingScreen';
import BluetoothManager, {
  BluetoothEvents,
  BluetoothStates,
} from 'src/services/bluetooth/BluetoothManager';
import MachineStore from 'src/stores/MachineStore';
import MachineActions from 'src/actions/MachineActions';
import AccountStore from 'src/stores/AccountStore';
import Settings from 'src/Settings';
import BeaconDevice from 'src/services/bluetooth/BeaconDevice';
import BluetoothDeviceTypes from 'src/constants/BluetoothDeviceTypes';
import Util from 'src/Util';
import {Location} from 'src/types/serverTypes/Location';
import {useAppSelector} from 'src/redux/hooks';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import ActionsFactory from 'src/actions/ActionsFactory';
import CartTypes from 'src/constants/cart/CartTypes';
import Events from 'src/logging/Events';
import {MachineType} from 'src/types/MachineType';
import MainConsumerContext from 'src/components/MainConsumerContext';
import TransactionStore from 'src/stores/TransactionStore';
import TransactionActions from 'src/actions/TransactionActions';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import Styles from 'src/components/Styles';
import {getDescriber as getSnackDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import NoMobileOrderLocation from 'src/components/img/svg/NoMobileOrderLocation';
import {getDescriber as getOnboardingDescriber} from 'src/components/elements/descriptor/DescriptorType';
import {getDescriber as getPickupLocationDescriber} from 'src/components/screens/orderAhead/descriptor/DescriptorType';
import {getDescriber} from 'src/components/screens/descriptor/DescriptorType';
import {getButtonDescriber} from 'src/components/elements/buttonDescriptor/DescriptorType';
import {getHeaderDescriber} from 'src/components/elements/headerDescriptor/DescriptorType';

type RewardsScanProductScreenProps = {
  productName: string;
  point: number;
  imageUrl: string;
  rewardProductId: string;
  productId: string;
  upc?: string[];
  cancel: () => void;
} & IsConnectedProps;

const RewardsScanProductScreen = (props: RewardsScanProductScreenProps) => {
  const isHomeLocationSma = useAppSelector((s) => s.account.isHomeLocationSma);
  const homeOrgId = useAppSelector((s) => s.account.account.orgId);
  const context = useContext(MainConsumerContext);

  const [bluetoothState, setBluetoothState] = useState(
    BluetoothStates?.PoweredOff,
  );
  const [locations, setLocations] = useState<Array<CampusLocationType>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isUnSupportedLocation, setIsUnSupportedLocation] =
    useState<boolean>(false);

  useEffect(() => {
    FirebaseAnalytic.trackEvent(
      'componentDidMount',
      'RewardsScanProductScreen',
      {
        ...props,
      },
    );
    MachineStore.addChangeListener(onMachineStoreChange);
    BluetoothManager.addListener(
      BluetoothEvents.onBTStateUpdate,
      onDeviceFound,
    );
    BluetoothManager.addListener(BluetoothEvents.onDeviceFound, onDeviceFound);
    requestPermissions();

    return () => {
      MachineStore.removeChangeListener(onMachineStoreChange);
      BluetoothManager.removeListener(
        BluetoothEvents.onBTStateUpdate,
        onDeviceFound,
      );
      BluetoothManager.removeListener(
        BluetoothEvents.onDeviceFound,
        onDeviceFound,
      );
    };
  }, []);

  const onDeviceFound = async (device: {type: string}) => {
    if (device.type === BluetoothDeviceTypes.beacon) {
      const beacon = device as BeaconDevice;
      MachineActions.marketScanned(
        beacon.beaconId,
        AccountStore.getAccountId(),
        Settings.buildType,
      );
    }
  };

  const checkBluetoothState = async () => {
    const currentState = await BluetoothManager.getBluetoothState();
    if (currentState !== bluetoothState) {
      setBluetoothState(currentState);
      if (currentState === BluetoothStates?.PoweredOn) {
        BluetoothManager.startScan();
      } else {
        BluetoothManager.stopScan();
        MachineActions.clearMarkets();
        if (NavActions.getLastRoute() === AppRoutes.NewHome) {
          MachineActions.clearMachines();
          MachineActions.clearDevices();
        }
      }
    }
  };

  const requestPermissions = async () => {
    if (Platform.OS === 'android') {
      PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
      ).then((result) => {
        if (result === 'granted') {
          PermissionsAndroid.requestMultiple([
            PermissionsAndroid?.PERMISSIONS?.BLUETOOTH_SCAN,
            PermissionsAndroid?.PERMISSIONS?.BLUETOOTH_CONNECT,
            PermissionsAndroid?.PERMISSIONS?.BLUETOOTH_ADVERTISE,
          ]).then(() => {
            checkBluetoothState();
          });
        }
      });
    } else {
      checkBluetoothState();
    }
    setLoading(false);
  };

  const onMachineStoreChange = () => {
    getMarketLocations();
  };

  const getMarketLocations = async () => {
    const locationList = await Util.filterLocationsPerBalanceType(
      isHomeLocationSma,
      homeOrgId,
      MachineStore.getMarkets() as unknown as Location[],
    );
    const marketsList =
      locationList.supportedLocations as unknown as CampusLocationType[];
    setLocations([...marketsList]);
    setIsUnSupportedLocation(locationList?.unSupportedLocations.length > 0);
  };

  const onLocationClick = async (machine: CampusLocationType) => {
    try {
      TransactionActions.shoppingCartCleared();
      context?.actions.showSpinner();
      const locationResponse =
        await ActionsFactory.getAccountActions().getLocation(machine.id);
      const parameters = {
        cartType: CartTypes.ScanAndPay,
        location: {...machine, ...locationResponse},
        locationId: locationResponse.locationId,
      };
      startCartSession(AppRoutes.ScanAndPay, machine, parameters);
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'RewardsScanProductScreen:onLocationClick',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'RewardsScanProductScreen:onLocationClick',
        generateErrorMessage(error),
      );
    } finally {
      context?.actions.hideSpinner();
    }
  };

  const startCartSession = (
    route: AppRoutes,
    machine: MachineType,
    additionalParams?: object,
    key?: string,
  ) => {
    const param = context?.actions.getScanScreenParams(machine);
    Events.Machines.trackEvent('startCartSession', {
      param,
      additionalParams,
      route,
    });
    TransactionStore.setRewardItem({
      BarCode: props.upc[0],
      Id: props.productId,
      Name: props.productName,
      isFreeRewardProduct: true,
    });
    NavActions.push(route, {...param, ...additionalParams}, key);
  };

  const getInstructionComponent = () => {
    return (
      <Text
        nativeID={`Reward_Scan_Product_${Localized.Labels.instructions}`}
        maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
        style={[
          styles.instructionTextTitle,
          getSnackDescriber()?.snackDetailsBtnFontSTyle(),
        ]}
      >
        {`${Localized.Labels.instructions}: `}
        <Text
          style={styles.instructionText}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
        >
          {Localized.Labels.start_by_choosing_a_market_description}
        </Text>
      </Text>
    );
  };

  const getNoLocationsView = () => {
    return (
      <View style={styles.noLocationsContainer}>
        <View style={styles.noLocationsView}>
          <NoMobileOrderLocation
            color={getButtonDescriber().redeemRewardIconColor()}
          />
          <Text
            style={[
              getOnboardingDescriber().onboardingFlowStyleDescriptor()
                .descriptionText,
              styles.errorTitleText,
            ]}
            accessible={false}
            accessibilityElementsHidden={true}
            accessibilityLabel={
              Localized.Labels.there_are_no_locations_near_you
            }
            nativeID={Localized.Labels.there_are_no_locations_near_you}
            accessibilityRole="text"
            aria-label={`${Localized.Labels.there_are_no_locations_near_you}, text`}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
          >
            {Localized.Labels.there_are_no_locations_near_you}
          </Text>
          <Text
            style={[styles.errorDescriptionText]}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
          >
            {Localized.Labels.please_move_closer_to_market_description}
          </Text>
        </View>
        <View
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
          style={[
            getPickupLocationDescriber().getPickupLocationStyles()
              .buttonContainerStyle,
            styles.buttonContainer,
          ]}
        >
          <RoundedAppButton
            buttonType={RoundedAppButtonType.Solid}
            onPress={() => NavActions.pop()}
            buttonViewStyle={
              getPickupLocationDescriber().getPickupLocationStyles().buttonView
            }
            buttonContainerStyle={
              getPickupLocationDescriber().getPickupLocationStyles()
                .containerStyle
            }
            accessibilityLabelValue={Localized.Buttons.goback_to_rewards}
            titleText={Localized.Buttons.goback_to_rewards}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
            textStyle={[
              styles.buttonText,
              getPickupLocationDescriber().getPickupLocationStyles().buttonText,
              getDescriber().getLetsGoStyle(),
            ]}
          />
        </View>
      </View>
    );
  };

  return loading ? (
    <LoadingScreen />
  ) : (
    <BaseScreen
      title={Localized.Labels.scan_product_title}
      accessibilityLabel={'Back arrow'}
      accessibilityHint={'Press to navigate back to the Home screen'}
    >
      {getInstructionComponent()}
      {locations.length > 0 ? (
        <>
          <Text
            nativeID={Localized.Labels.markets}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
            style={[
              styles.locationTitle,
              getSnackDescriber()?.snackDetailsBtnFontSTyle(),
              getHeaderDescriber().subHeaderContainerStyleDescriptor()
                .subHeaderTextStyle,
            ]}
          >
            {Localized.Labels.markets}
          </Text>
          <ScanAndPayLocationsList
            locations={locations}
            isConnected={true}
            onLocationClick={onLocationClick}
            scrollEnabled={true}
            coffeeLocations={[]}
            isUnSupportedLocation={isUnSupportedLocation}
          />
        </>
      ) : (
        getNoLocationsView()
      )}
    </BaseScreen>
  );
};

const styles = StyleSheet.create({
  instructionTextTitle: {
    fontWeight: '700',
    fontSize: Styles.Fonts.f1,
    color: Styles.black,
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginTop: Styles.Spacing.m3,
  },
  instructionText: {
    fontWeight: '400',
  },
  locationTitle: {
    color: Styles.black,
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginTop: Styles.Spacing.m3,
  },
  errorTitleText: {
    fontWeight: '700',
  },
  noLocationsContainer: {
    flex: 1,
    alignItem: 'center',
    justifyContent: 'flex-start',
    alignSelf: 'center',
  },
  noLocationsView: {
    marginTop: Styles.Spacing.m4,
    alignItems: 'center',
  },
  errorDescriptionText: {
    fontWeight: '400',
    fontSize: Styles.Fonts.f7,
    color: Styles.black,
    textAlign: 'center',
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m3,
    marginTop: Styles.Spacing.m3,
  },
  buttonContainer: {
    position: 'absolute',
    bottom: Styles.Spacing.m3,
  },
  buttonText: {
    textTransform: 'none',
  },
});

export default withForwardedNavigationParams()(
  withIsConnected(RewardsScanProductScreen),
);
